// ─────────────────────────────────────────────────────────────────────────────
// vault_dispatch.ts — runtime selector for the vault backend.
//
// Re-exports the same public surface as `persistence.ts` (the IDB backend)
// but at runtime can route every call to either:
//   - IndexedDB  (default; works in every browser, ephemeral per-origin)
//   - File System Access API directory  (user-picked folder, OneDrive/Drive
//     friendly, Chromium-only)
//
// The active backend is set via `setActiveHandle(handle | null)`. When set,
// every subsequent persistence call routes to the FSA adapter against that
// handle. When cleared, calls route back to IDB.
//
// `standalone_host.ts` imports this module under the same `persistence`
// namespace it used before — no call-site changes needed.
// ─────────────────────────────────────────────────────────────────────────────

import type { GrimoireAction } from '@shared/grimoire_action';
import * as idb from './persistence';
import * as fsa from './vault_fs_adapter';

// Re-export types so consumers can keep importing from one place.
export type {
  NotebookIndex, SerialPageEntry, SerialSectionEntry, StrokeData,
} from './persistence';

let _activeHandle: FileSystemDirectoryHandle | null = null;

/** Switch the persistence backend. Pass null to revert to IndexedDB. */
export function setActiveHandle(handle: FileSystemDirectoryHandle | null): void {
  _activeHandle = handle;
}

/** Currently active vault folder handle, if any. */
export function getActiveHandle(): FileSystemDirectoryHandle | null {
  return _activeHandle;
}

/** True when the FSA backend is active. */
export function isFsaActive(): boolean {
  return _activeHandle !== null;
}

// ── Dispatched API (matches persistence.ts surface) ─────────────────────────

export async function saveIndex(index: idb.NotebookIndex): Promise<void> {
  if (_activeHandle) return fsa.saveIndex(_activeHandle, index);
  return idb.saveIndex(index);
}

export async function loadIndex(): Promise<idb.NotebookIndex | null> {
  if (_activeHandle) return fsa.loadIndex(_activeHandle);
  return idb.loadIndex();
}

export async function appendAction(action: GrimoireAction): Promise<void> {
  if (_activeHandle) return fsa.appendAction(_activeHandle, action);
  return idb.appendAction(action);
}

export async function loadAllActions(): Promise<GrimoireAction[]> {
  if (_activeHandle) return fsa.loadAllActions(_activeHandle);
  return idb.loadAllActions();
}

export async function savePageStrokes(pageId: string, strokes: idb.StrokeData[]): Promise<void> {
  if (_activeHandle) return fsa.savePageStrokes(_activeHandle, pageId, strokes);
  return idb.savePageStrokes(pageId, strokes);
}

export async function loadPageStrokes(pageId: string): Promise<idb.StrokeData[]> {
  if (_activeHandle) return fsa.loadPageStrokes(_activeHandle, pageId);
  return idb.loadPageStrokes(pageId);
}

export async function saveAllStrokes(
  pageStrokes: Map<string, Map<string, idb.StrokeData>>,
): Promise<void> {
  if (_activeHandle) return fsa.saveAllStrokes(_activeHandle, pageStrokes);
  return idb.saveAllStrokes(pageStrokes);
}

export async function saveSettings(settings: Record<string, unknown>): Promise<void> {
  if (_activeHandle) return fsa.saveSettings(_activeHandle, settings);
  return idb.saveSettings(settings);
}

export async function loadSettings(): Promise<Record<string, unknown> | null> {
  if (_activeHandle) return fsa.loadSettings(_activeHandle);
  return idb.loadSettings();
}

export async function hasNotebook(): Promise<boolean> {
  if (_activeHandle) return fsa.hasNotebook(_activeHandle);
  return idb.hasNotebook();
}

export async function clearAll(): Promise<void> {
  if (_activeHandle) return fsa.clearAll(_activeHandle);
  return idb.clearAll();
}
